import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ColabVideoService, VideoGenerationParams, VideoGenerationResponse } from '../services/colab-video.service';
import { ToastService } from '../services/toast.service';
import { interval, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';

@Component({
  selector: 'app-video-module',
  templateUrl: './video-module.component.html',
  styleUrls: ['./video-module.component.scss']
})
export class VideoModuleComponent implements OnInit {
  @ViewChild('videoPlayer') videoPlayer!: ElementRef;

  // Forms
  videoForm: FormGroup;
  colabUrlControl = new FormControl('', [Validators.required, Validators.pattern('https?://.+')]);

  // UI States
  isDarkMode = false;
  isGenerating = false;
  showColabUrlInput = false;
  videoGenerated = false;
  videoLoadError = false;
  errorMessage = '';
  processingProgress = 0;
  processingStep = '';
  estimatedTime = 0;
  remainingTime = 0;
  private progressSubscription?: Subscription;

  // Video Options
  resolutionOptions = [
    { value: '848x480', label: '848x480 (Standard Landscape)' },
    { value: '960x544', label: '960x544 (HD Landscape)' },
    { value: '1280x720', label: '1280x720 (HD Landscape)' },
    { value: '960x960', label: '960x960 (Square)' },
    { value: '720x720', label: '720x720 (Square)' },
    { value: '544x960', label: '544x960 (Portrait)' },
    { value: '720x1280', label: '720x1280 (Portrait)' },
    { value: '832x624', label: '832x624 (4:3 Landscape)' },
    { value: '624x832', label: '624x832 (3:4 Portrait)' },
    { value: '1104x832', label: '1104x832 (Wide Landscape)' },
    { value: '832x1104', label: '832x1104 (Tall Portrait)' }
  ];

  // Video URL
  safeVideoUrl: SafeUrl | null = null;

  constructor(
    private location: Location,
    private formBuilder: FormBuilder,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private colabVideoService: ColabVideoService,
    private toastService: ToastService
  ) {
    this.initializeForm();
    this.loadDarkModePreference();
  }

  ngOnInit() {
    this.checkColabUrl();
    this.setupFormValueChanges();
  }

  private setupFormValueChanges() {
    this.videoForm.valueChanges.subscribe((values) => {
      this.updateEstimatedTime(values);
    });
  }

  private updateEstimatedTime(params: VideoGenerationParams) {
    this.estimatedTime = this.colabVideoService.estimateGenerationTime(params);
  }

  private initializeForm() {
    this.videoForm = this.formBuilder.group({
      prompt: ['', [Validators.required, Validators.minLength(10)]],
      video_length: [{value: 32, disabled: this.isGenerating}, [Validators.required, Validators.min(16), Validators.max(256)]]
    });
  }

  private loadDarkModePreference() {
    this.isDarkMode = localStorage.getItem('darkMode') === 'true';
  }

  toggleDarkMode() {
    this.isDarkMode = !this.isDarkMode;
    localStorage.setItem('darkMode', this.isDarkMode.toString());
  }

  private checkColabUrl() {
    this.colabVideoService.getColabUrl().subscribe({
      next: (url) => {
        if (!url) {
          this.showColabUrlInput = true;
        }
      },
      error: () => {
        this.showColabUrlInput = true;
      }
    });
  }

  setColabUrl() {
    if (this.colabUrlControl.valid) {
      this.colabVideoService.setColabUrl(this.colabUrlControl.value!).subscribe({
        next: () => {
          this.showColabUrlInput = false;
          this.toastService.show('Colab URL set successfully', 'success');
        },
        error: (error) => {
          this.toastService.show('Failed to set Colab URL: ' + error.message, 'error');
        }
      });
    }
  }

  generateVideo() {
    if (this.videoForm.valid) {
      this.isGenerating = true;
      this.videoGenerated = false;
      this.videoLoadError = false;
      this.errorMessage = '';
      this.processingProgress = 0;
      this.processingStep = 'Initializing video generation...';

      // Combine form values with default parameters
      const params: VideoGenerationParams = {
        prompt: this.videoForm.get('prompt')?.value || '',
        video_length: Number(this.videoForm.get('video_length')?.value || 32),
        resolution: '848x480',
        num_inference_steps: 11,
        guidance_scale: 1.0,
        flow_shift: 7.0,
        embedded_guidance_scale: 6.0
      };

      this.estimatedTime = this.colabVideoService.estimateGenerationTime(params);
      this.remainingTime = this.estimatedTime;

      // Set up progress tracking
      this.setupProgressTracking();

      this.colabVideoService.generateVideo(params).subscribe({
        next: (response) => {
          this.cleanupProgressTracking();
          this.processingProgress = 100;
          this.processingStep = 'Finalizing video...';
          this.remainingTime = 0;
          
          setTimeout(() => {
            this.handleVideoGenerated(response);
          }, 1000);
        },
        error: (error) => {
          this.cleanupProgressTracking();
          this.handleError(error);
        }
      });
    }
  }

  private setupProgressTracking() {
    const updateInterval = 1000; // 1 second
    const totalTime = this.estimatedTime * 1000; // Convert to milliseconds
    
    this.progressSubscription = interval(updateInterval)
      .pipe(takeWhile(() => this.isGenerating))
      .subscribe(() => {
        if (this.remainingTime > 0) {
          this.remainingTime--;
          this.processingProgress = ((this.estimatedTime - this.remainingTime) / this.estimatedTime) * 100;
          this.updateProcessingStep();
        }
      });
  }

  private cleanupProgressTracking() {
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
    }
  }

  private updateProcessingStep() {
    const steps = [
      'Processing prompt...',
      'Generating frames...',
      'Applying motion model...',
      'Enhancing video quality...',
      'Finalizing video...'
    ];
    
    const stepIndex = Math.floor((this.processingProgress / 100) * steps.length);
    this.processingStep = steps[Math.min(stepIndex, steps.length - 1)];
  }

  private handleVideoGenerated(response: VideoGenerationResponse) {
    if (!response || !response.success) {
      this.handleError(new Error('Video generation was not successful'));
      return;
    }

    // Get the Colab URL from storage
    const colabUrl = localStorage.getItem(this.colabVideoService.getStorageKey());
    if (!colabUrl) {
      this.handleError(new Error('Colab URL not found'));
      return;
    }

    // Remove any trailing slashes from the Colab URL
    const baseUrl = colabUrl.replace(/\/+$/, '');
    
    // Extract the full path from the result message
    const fullPath = response.result.match(/Path: (.*\.mp4)/)?.[1];
    if (!fullPath) {
      this.handleError(new Error('Could not extract video path from response'));
      return;
    }

    // Construct the video URL using the verified working format
    const videoUrl = `${baseUrl}/gradio_api/file=${fullPath}`;
    console.log('Using video URL:', videoUrl);

    // Verify the URL is accessible
    fetch(videoUrl, { method: 'HEAD' })
      .then((fetchResponse: Response) => {
        if (fetchResponse.ok) {
          this.safeVideoUrl = this.sanitizer.bypassSecurityTrustUrl(videoUrl);
          this.isGenerating = false;
          this.videoGenerated = true;
          this.toastService.show('Video generated successfully!', 'success');
        } else {
          throw new Error('Video URL is not accessible');
        }
      })
      .catch(error => {
        console.error('Video URL verification failed:', error);
        this.handleError(new Error('Generated video is not accessible. Please try again.'));
      });
  }

  private handleError(error: any) {
    this.isGenerating = false;
    this.errorMessage = error.message || 'An error occurred while generating the video';
    this.toastService.show(this.errorMessage, 'error');
    console.error('Video generation error:', error);
  }

  handleVideoError(event: any) {
    console.error('Video loading error:', event);
    this.videoLoadError = true;
    this.videoGenerated = false;
  }

  downloadVideo() {
    if (this.safeVideoUrl) {
      const link = document.createElement('a');
      link.href = this.safeVideoUrl.toString();
      link.download = `generated-video-${Date.now()}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  regenerateVideo() {
    this.videoGenerated = false;
    this.videoLoadError = false;
    this.safeVideoUrl = null;
    this.errorMessage = '';
  }

  resetForm() {
    this.videoForm.reset({
      prompt: '',
      video_length: 32
    });
  }

  resetSettings() {
    this.resetForm();
    this.toastService.show('Settings reset to defaults', 'info');
  }

  openVideoModuleGuide() {
    // Implement video module guide dialog
  }

  openPromptGuide() {
    // Implement prompt guide dialog
  }

  cancelGeneration() {
    this.cleanupProgressTracking();
    this.isGenerating = false;
    this.processingProgress = 0;
    this.remainingTime = 0;
    this.toastService.show('Video generation cancelled', 'info');
  }

  goBack() {
    this.location.back();
  }

  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
} 